import React, { useState, useEffect } from 'react';
import { Typography, FlexGrid, Notification } from '@telus-uds/components-web';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

import { reportsAPI, exportReportExcel, exportReportCsv } from '../utils/apiService';
import { formatNumber, getDefaultDateRange } from '../utils/formatters';
import SummaryCard from '../components/SummaryCard';
import { orderTypeName, ORDER_TYPE_FILTER_OPTIONS } from '../utils/orderTypeLabels';
import { LANGUAGE_FILTER_OPTIONS } from '../utils/filterOptions';
import useReportingStrings from '../utils/useReportingStrings';
import ChartDescription from '../components/ChartDescription';
import CategoryAxisTick from '../components/CategoryAxisTick';
import LastUpdated from '../components/LastUpdated';
import BaselineBadge from '../components/BaselineBadge';
import ShareBarChart, { toShareData } from '../components/ShareBarChart';
import FilterPanel from '../components/FilterPanel';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import DrillDownModal from '../components/DrillDownModal';
import { chartCardStyle } from '../utils/chartStyles';

const ORDER_STATUSES = [
  { value: '', label: 'All' },
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'PENDING_VALIDATION', label: 'Pending Validation' },
  { value: 'ORDER_VALIDATED', label: 'Order Validated' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ACTION_REQUIRED', label: 'Action Required' },
  { value: 'CONTRACT_SENT', label: 'Contract Sent' },
  { value: 'CONTRACT_SIGNED', label: 'Contract Signed' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ORDER_CANCELED', label: 'Order Canceled' },
  { value: 'WAITING_ON_SALES', label: 'Waiting on Sales' },
  { value: 'WAITING_ON_CREDIT', label: 'Waiting on Credit' },
  { value: 'WAITING_ON_CUSTOMER_RESPONSE', label: 'Waiting on Customer Response' }
];

const PROVINCES = [
  { value: '', label: 'All' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon Territory' }
];

const OrderActivityReport = () => {
  const strings = useReportingStrings();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [drillDownType, setDrillDownType] = useState(null);
  const [drillDownChannel, setDrillDownChannel] = useState(null);
  const [channelOptions, setChannelOptions] = useState([{ value: '', label: 'All' }]);
  const [filterValues, setFilterValues] = useState({
    startDate: getDefaultDateRange().startDate,
    endDate: getDefaultDateRange().endDate,
    orderType: '',
    status: '',
    province: '',
    language: '',
    channelId: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(null);

  // refresh=true asks the server to skip its cached result (Refresh button).
  const fetchData = async (customFilters = null, refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const filters = customFilters || filterValues;
      setAppliedFilters(filters);
      const result = await reportsAPI.getOrderActivity(refresh ? { ...filters, refresh: true } : filters);
      setData(result.data);
      setMeta({ generatedAt: result.generated_at, throttled: Boolean(result.refresh_throttled) });
    } catch (err) {
      setError(err.message || 'Failed to fetch order activity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  useEffect(() => {
    reportsAPI.getChannels()
      .then(result => {
        const channels = (result && result.channels) || [];
        setChannelOptions([
          { value: '', label: 'All' },
          ...channels.map(c => ({ value: c.id, label: c.description || c.name }))
        ]);
      })
      .catch(() => {
        // Non-critical: leave the Channel filter as "All" only if the list fails to load.
      });
  }, []);

  const handleFilterChange = (name, value) => {
    setFilterValues(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      startDate: getDefaultDateRange().startDate,
      endDate: getDefaultDateRange().endDate,
      orderType: '',
      status: '',
      province: '',
      language: '',
      channelId: ''
    };
    setFilterValues(defaultFilters);
    // Fetch data immediately with default filters
    fetchData(defaultFilters);
  };

  // Uses the applied-filters snapshot, never unapplied edits (same rule as the Excel export).
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await exportReportCsv('/reports/order-activity/export/csv', appliedFilters || filterValues, 'order-activity');
    } catch (err) {
      alert('Failed to export CSV file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const exportFilters = appliedFilters || filterValues;
      const fileName = `order-activity-report-${exportFilters.startDate}-to-${exportFilters.endDate}.xlsx`;
      await exportReportExcel('/reports/order-activity/export/excel', exportFilters, fileName);
    } catch (err) {
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const filters = [
    {
      name: 'startDate',
      label: 'Start Date',
      type: 'date',
      value: filterValues.startDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'endDate',
      label: 'End Date',
      type: 'date',
      value: filterValues.endDate,
      placeholder: 'YYYY-MM-DD'
    },
    {
      name: 'orderType',
      label: 'Order Type',
      type: 'select',
      value: filterValues.orderType,
      options: ORDER_TYPE_FILTER_OPTIONS
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filterValues.status,
      options: ORDER_STATUSES
    },
    {
      name: 'province',
      label: 'Province',
      type: 'select',
      value: filterValues.province,
      options: PROVINCES
    },
    {
      name: 'language',
      label: 'Language',
      type: 'select',
      value: filterValues.language,
      options: LANGUAGE_FILTER_OPTIONS
    },
    {
      name: 'channelId',
      label: 'Channel',
      type: 'select',
      value: filterValues.channelId,
      options: channelOptions
    }
  ];

  if (loading) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <LoadingSpinner text="Loading Order Activity Report..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <Notification variant={{ validation: 'error' }}>
          <div>
            <strong>Error:</strong> {error}
          </div>
        </Notification>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        <FilterPanel filters={filters} onFilterChange={handleFilterChange} onApply={handleApplyFilters} onReset={handleResetFilters} />
        <EmptyState title="No Data Available" />
      </div>
    );
  }

  const {
    total_orders, submitted: submittedOrders, by_type, by_status, by_province, daily_distribution,
    by_language: byLanguage, by_channel: byChannel, language_backfill_pending: languageBackfillPending
  } = data;

  // name is what the user sees (full order type name); rawValue is the code the drill-down needs.
  const orderTypeData = (by_type || []).map(item => ({
    name: orderTypeName(item.type_of_order || item.label) || 'Unknown',
    rawValue: item.type_of_order || item.label,
    submitted: parseInt(item.submitted, 10) || 0,
    value: parseInt(item.count, 10)
  }));

  const statusData = (by_status || []).map(item => ({
    name: item.label || 'Unknown',
    count: parseInt(item.count, 10)
  }));

  const provinceData = (by_province || []).map(item => ({
    name: item.label || 'Unknown',
    value: parseInt(item.count, 10)
  }));

  const languageData = (byLanguage || []).map(item => ({ name: item.label, rawValue: item.language, value: item.count }));
  const channelData = (byChannel || []).map(item => ({
    name: item.label, rawValue: item.channel_id, submitted: item.submitted, value: item.count
  }));

  const dailyData = (daily_distribution || [])
    .slice()
    .reverse()
    .map(item => ({
      date: item.date ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A',
      count: parseInt(item.count, 10)
    }));

  const topType = orderTypeData.slice().sort((a, b) => b.value - a.value)[0];
  const topProvince = provinceData.slice().sort((a, b) => b.value - a.value)[0];

  return (
    <div>
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        onExport={handleExportExcel}
        onExportCsv={handleExportCsv}
        isExporting={isExporting}
      />

      <LastUpdated
        generatedAt={meta && meta.generatedAt}
        throttled={Boolean(meta && meta.throttled)}
        loading={loading}
        onRefresh={() => fetchData(null, true)}
      />

      {/* Summary Cards */}
      <FlexGrid>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Orders Created" descriptionKey="ordersCreated"
                extra={<BaselineBadge metric={data.metrics && data.metrics.orders_created} baseline={data.baseline} />}
              value={formatNumber(total_orders)}
              icon="📦"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Orders Submitted" descriptionKey="ordersSubmitted"
                extra={<BaselineBadge metric={data.metrics && data.metrics.orders_submitted} baseline={data.baseline} />}
              value={formatNumber(submittedOrders)}
              icon="📨"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Top Order Type" descriptionKey="topOrderType"
              value={(topType && topType.name) || 'N/A'}
              subText={topType ? `${formatNumber(topType.value)} orders` : undefined}
              icon="🏆"
            />
          </FlexGrid.Col>
          <FlexGrid.Col xs={12} md={6} lg={4} style={{ padding: '0 0.75rem', marginBottom: '1.5rem' }}>
            <SummaryCard
              title="Top Province" descriptionKey="topProvince"
              value={(topProvince && topProvince.name) || 'N/A'}
              subText={topProvince ? `${formatNumber(topProvince.value)} orders` : undefined}
              icon="📍"
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      {/* Charts Section */}
      <FlexGrid style={{ marginTop: '2rem' }}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Type
              </Typography>
              <ChartDescription descriptionKey="ordersByType" />
              <ShareBarChart data={toShareData(orderTypeData)} title="Orders by Type" onBarClick={setDrillDownType} showSubmitted />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Status
              </Typography>
              <ChartDescription descriptionKey="ordersByStatus" />
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statusData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" height={90} interval={0} tickLine={false} tick={<CategoryAxisTick />} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#0066CC" name="Order Count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>

        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Province
              </Typography>
              <ChartDescription descriptionKey="ordersByProvince" />
              <ShareBarChart data={toShareData(provinceData)} title="Orders by Province" />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div
              style={chartCardStyle}
            >
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Daily Order Volume
              </Typography>
              <ChartDescription descriptionKey="dailyOrderVolume" />
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#4b286d"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div style={chartCardStyle}>
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Language
              </Typography>
              <ChartDescription descriptionKey="ordersByLanguage" />
              {languageBackfillPending && (
                <p data-testid="language-backfill-notice" style={{ color: '#666666', fontSize: '0.875rem' }}>
                  {strings.languageBackfillPending}
                </p>
              )}
              <ShareBarChart data={toShareData(languageData)} title="Orders by Language" />
            </div>
          </FlexGrid.Col>

          <FlexGrid.Col xs={12} md={6} style={{ padding: '0 0.75rem', marginBottom: '2rem' }}>
            <div style={chartCardStyle}>
              <Typography heading="h3" variant={{ size: 'h3' }} block style={{ marginBottom: '1rem', color: '#4b286d' }}>
                Orders by Channel
              </Typography>
              <ChartDescription descriptionKey="ordersByChannel" />
              <ShareBarChart
                data={toShareData(channelData)}
                title="Orders by Channel"
                showSubmitted
                onBarClick={key => { if (key !== 'none') setDrillDownChannel(key); }}
              />
            </div>
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      <DrillDownModal
        open={!!drillDownType}
        onClose={() => setDrillDownType(null)}
        title={`Orders — ${orderTypeName(drillDownType)}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          orderType: drillDownType,
          status: filterValues.status,
          province: filterValues.province,
          language: filterValues.language,
          channelId: filterValues.channelId
        }}
        fetchRecords={reportsAPI.getOrderActivityRecords}
        columns={[
          { key: 'order_id', label: 'Order ID' },
          { key: 'type_of_order', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'province', label: 'Province' },
          { key: 'legal_business_name', label: 'Business Name' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
      <DrillDownModal
        open={!!drillDownChannel}
        onClose={() => setDrillDownChannel(null)}
        title={`Orders — ${((channelData.find(c => c.rawValue === drillDownChannel)) || {}).name || drillDownChannel}`}
        queryParams={{
          startDate: filterValues.startDate,
          endDate: filterValues.endDate,
          orderType: filterValues.orderType,
          status: filterValues.status,
          province: filterValues.province,
          language: filterValues.language,
          channelId: drillDownChannel
        }}
        fetchRecords={reportsAPI.getOrderActivityRecords}
        columns={[
          { key: 'order_id', label: 'Order ID' },
          { key: 'type_of_order', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'province', label: 'Province' },
          { key: 'legal_business_name', label: 'Business Name' },
          { key: 'created_at', label: 'Created At' }
        ]}
      />
    </div>
  );
};

export default OrderActivityReport;
